<template>
  <v-container class="py-6" grid-list-md>
    <v-layout column>

      <p>Immersive Tendencies Questionnaire (ITQ) je krátky psychologický dotazník, ktorý hodnotí mieru, do akej sa človek prirodzene „ponára“ do médií a činností (filmy, hry, príbehy, šport), ako aj jeho schopnosť udržať pozornosť a emocionálne reagovať na obsah. Skóre v troch oblastiach – zahĺbenie (Involvement), sústredenie (Focus) a emočná reaktivita (Emotion) - nepredstavujú diagnózu; ide o výskumný ukazovateľ individuálnych rozdielov v prežívaní a pozornosti.</p>
      <p>Výsledky plánujeme využívať na prispôsobovanie obsahu: účastníkom s vyšším zahĺbením a emočnou reaktivitou budeme ponúkať naratívne bohatšie, pohlcujúce materiály, kým pri vyššom sústredení a nižšej emočnej reaktivite uprednostníme analytickejšie, vecné podklady. Cieľom je zvýšiť relevantnosť, angažovanosť a komfort pri práci s obsahom, pričom personalizácia bude prebiehať anonymizovane a v súlade so súhlasom respondenta.</p>
      <v-form ref="form" v-model="valid" @submit.prevent="onSubmit">
        <!-- Položky -->
        <v-card
          v-for="(q, idx) in items"
          :key="q.code"
          outlined
          class="pa-4 mb-4"
        >
          <div class="subtitle-1 font-weight-medium mb-2">
            {{ idx + 1 }}. {{ q.text }}
          </div>
          <v-radio-group
            :value="answers[q.code]"
            @change="setAnswer(q.code, $event)"
            :rules="[v => !!v || 'Vyberte jednu možnosť']"
          >
            <v-radio
              v-for="(opt, i) in q.options"
              :key="i"
              :label="opt"
              :value="i+1"  
            />
          </v-radio-group>
        </v-card>

        <div class="d-flex align-center justify-space-between">
          <div class="caption grey--text">
            Vyplnené: {{ answeredCount }}/{{ items.length }}
          </div>
          <div v-if="!submitted">
            <v-btn color="primary" type="submit" class="mr-2">Odoslať a vyhodnotiť</v-btn>
            <v-btn color="primary" text @click="resetForm">Vyčistiť</v-btn>
          </div>
        </div>
      </v-form>

      <!-- Výsledky -->
      <div v-if="submitted" class="mt-6">
        <h2 class="title font-weight-medium mb-3">Výsledky (3-faktorový profil)</h2>

        <v-layout row wrap>
          <v-flex xs12 sm4 v-for="k in factors" :key="k">
            <v-card outlined class="pa-3 text-xs-center">
              <div class="caption grey--text text--darken-1">{{ factorLabel[k] }}</div>
              <div class="headline font-weight-bold">{{ means[k].toFixed(2) }}</div>
              <div class="caption grey--text text--darken-1">priemer (1–7)</div>
            </v-card>
          </v-flex>
        </v-layout>

        <!-- Radarový graf -->
        <v-card outlined class="pa-3 mt-4">
          <div class="subtitle-2 mb-2">Profil tendencií (priemer 1-7 na faktor)</div>
          <div class="chart-wrap chart-sm">
            <canvas ref="radarCanvas"></canvas>
          </div>
          <div class="d-flex justify-end">
            <v-btn color="primary" type="submit" class="mr-2" @click="closeTest">Koniec</v-btn>
          </div>
        </v-card>


      </div>


    </v-layout>
  </v-container>
</template>

<script>
// a) npm variant (odporúčaný s Vue2/Webpack4 → Chart.js 3.9.1):
//    npm i chart.js@3.9.1
import axios from '../../axios-auth.js'
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables);

export default {
  name: 'ITQSurveySK',
  data () {
    const F = { INV:'INV', FOC:'FOC', EMO:'EMO' } // faktory
    return {
      valid: false,
      submitted: false,
      consent: null, // 'yes' | 'no'
      factors: [F.INV, F.FOC, F.EMO],
      factorLabel: {
        INV: 'Zahĺbenie / Involvement',
        FOC: 'Sústredenie / Focus',
        EMO: 'Emočná reaktivita'
      },
      // odpovede: 1..7 (alebo 1..2 pri súhlase, ten ale nie je v items)
      answers: {},
      agreement: 0,
      startTime: null,

      // položky (bez reverzov, 1 = nízke, 7 = vysoké)
      items: [
        // INV – frekvencie/absorpcia
        {
          code: 'ITQ01',
          factor: F.INV,
          text: 'Ľahko sa vžívate alebo intenzívne prežívate sledovanie filmov, seriálov alebo počítačových hier?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        {
          code: 'ITQ02',
          factor: F.INV,
          text: 'Zahĺbite sa niekedy do televízneho programu alebo knihy natoľko, že ľudia majú problém upútať vašu pozornosť?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        // FOC – aktuálna pozornosť (vyššie = lepšie)
        {
          code: 'ITQ03',
          factor: F.FOC,
          text: 'Nakoľko by ste ohodnotili svoju aktuálnu duševnú pozornosť (sústredenosť)?',
          options: ['úplne nesústredený','veľmi slabo sústredený','dosť nesústredený','priemerne sústredený','viac-menej sústredený','veľmi dobre sústredený','úplne sústredený']
        },
        // INV
        {
          code: 'ITQ04',
          factor: F.INV,
          text: 'Stáva sa vám niekedy, že sa tak zapozeráte do filmu alebo hry, že si neuvedomujete, čo sa okolo vás deje?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        {
          code: 'ITQ05',
          factor: F.INV,
          text: 'Ako často sa vám stáva, že sa úzko stotožňujete s postavami v príbehovej línii?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        {
          code: 'ITQ06',
          factor: F.INV,
          text: 'Stáva sa vám, že sa tak zahĺbite do počítačovej hry, že sa vám zdá, akoby ste boli v hre, a nielen pohybovali joystickom a sledovali obrazovku?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        // FIT – kontrolná (nezapočítava sa)
        {
          code: 'ITQ07',
          factor: F.FOC, // formálne by to šlo k FOC, ale NEPOČÍTAME ju
          exclude: true,
          text: 'Ako sa dnes cítite fyzicky fit?',
          options: ['úplne vyčerpaný','veľmi unavený','unavený','priemerne fit','viac-menej fit','veľmi dobre','úplne fit']
        },
        // FOC – odfiltrovanie rušenia
        {
          code: 'ITQ08',
          factor: F.FOC,
          text: 'Ako často dokážete odblokovať vonkajšie rušivé vplyvy, keď ste do niečoho zahĺbený?',
          options: ['vôbec','veľmi zriedkavo','väčšinou nie','priemerne','väčšinou áno','zvyčajne áno','vždy']
        },
        // INV – šport/embodiment
        {
          code: 'ITQ09',
          factor: F.INV,
          text: 'Stáva sa vám niekedy, že sa pri sledovaní športu zapojíte do hry natoľko, že reagujete, akoby ste boli jedným z hráčov?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        {
          code: 'ITQ10',
          factor: F.INV,
          text: 'Stáva sa vám niekedy, že sa tak zamyslíte (zasnívate) počas dňa, že si neuvedomujete, čo sa okolo vás deje?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        // EMO – sny/afekt
        {
          code: 'ITQ11',
          factor: F.EMO,
          text: 'Mávate niekedy sny, ktoré sú také skutočné, že sa cítite dezorientovaný, keď sa zobudíte?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        // INV – flow pri športe
        {
          code: 'ITQ12',
          factor: F.INV,
          text: 'Zapájate sa pri športovaní do hry natoľko, že strácate pojem o čase?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        // FOC – sústredenie na príjemné aktivity
        {
          code: 'ITQ13',
          factor: F.FOC,
          text: 'Ako dobre sa dokážete sústrediť na aktivity, ktoré u vás vyvolávajú príjemné pocity?',
          options: ['vôbec','veľmi slabo','slabo','priemerne','dobre','veľmi dobre','vynikajúco']
        },
        // GAMES – frekvencia (nezapočítava sa)
        {
          code: 'ITQ14',
          factor: F.INV, // voliteľne by mohol tvoriť samostatnú škálu „Games“, ale teraz ho NEPOČÍTAME
          exclude: true,
          text: 'Ako často hráte počítačové alebo komplexnejšie mobilné hry (nie príležitostné – do 10 minút v kuse)?',
          options: ['nikdy','zriedkavo','príležitostne','priemerne často','dosť často','veľmi často','každý alebo každý druhý deň']
        },
        // EMO – reaktivita na scény
        {
          code: 'ITQ15',
          factor: F.EMO,
          text: 'Rozrušili vás niekedy scény s naháňačkou alebo s bojom vo filmoch alebo v hrách?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        {
          code: 'ITQ16',
          factor: F.EMO,
          text: 'Vyľakali ste sa niekedy z toho, čo sa stalo vo filme alebo v hre?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        {
          code: 'ITQ17',
          factor: F.EMO,
          text: 'Stalo sa vám, že strach vo vás zostal dlho po dopozeraní strašidelného filmu?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        // INV – všeobecný flow
        {
          code: 'ITQ18',
          factor: F.INV,
          text: 'Stalo sa vám niekedy, že ste sa do niečoho zapojili natoľko, že ste stratili pojem o čase?',
          options: ['nikdy','zriedkavo (do 10% prípadov)','príležitostne (do 30% prípadov)','niekedy (do 50% prípadov)','často (do 70% prípadov)','zvyčajne (do 90% prípadov)','vždy']
        },
        { code: 'X', 
          factor: 'X',
          text: 'Súhlasím so spracovaním anonymizovaných údajov na vedecké účely:',
          options: ['áno súhlasím','nie nesúhlasím'],
        },
      ],

      means: { INV: 0, FOC: 0, EMO: 0 },
      radar: null
    }
  },
  computed: {
    answeredCount () {
      return Object.keys(this.answers).length
    }
  },
  mounted() {
    this.startTime = Date.now()
  },
  methods: {
  // --- helpers ---
  valOr(v, fb) { return (typeof v === 'number' && isFinite(v)) ? v : fb; },

  setAnswer (code, value) { this.$set(this.answers, code, value) },

  onSubmit () {      
    if (!this.$refs.form.validate()) return;

    // priemery (1–7), bez položiek s exclude=true a bez "X"
    const buckets = { INV: [], FOC: [], EMO: [] };
    this.items.forEach(q => {
      if (q.exclude) return;
      const v = this.answers[q.code];
      if (q.code === 'X') {
        this.agreement = v == 1 ? 1 : 0;
        return;
      }
      if (v != null && buckets[q.factor]) buckets[q.factor].push(Number(v));
    });
    const avg = arr => arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;
    this.means.INV = avg(buckets.INV);
    this.means.FOC = avg(buckets.FOC);
    this.means.EMO = avg(buckets.EMO);

    this.submitted = true;
    this.writeAnswer(1);

    // počkaj, kým sa do DOM-u vloží canvas
    this.$nextTick(() => this.renderRadar());
  },

  resetForm () {
    this.$refs.form.resetValidation();
    this.answers = {};
    this.submitted = false;
    this.means = { INV: 0, FOC: 0, EMO: 0 };
    this.destroyRadar();
  },

  // --- Radar (Chart.js 3.9.1) ---
  renderRadar () {
    const canvas = this.$refs.radarCanvas;
    if (!canvas) return;

    this.destroyRadar();

    const C = this.radarColorsUniversal();
    const labels = ['Zahĺbenie (INV)', 'Sústredenie (FOC)', 'Emócie (EMO)'];
    const data = [
      this.valOr(this.means.INV, 0),
      this.valOr(this.means.FOC, 0),
      this.valOr(this.means.EMO, 0),
    ];

    const ctx = canvas.getContext('2d');
    this.radar = new Chart(ctx, {
    type: 'radar',
    data: {
      labels,
      datasets: [{
        label: 'ITQ (1–7)',
        data,
        borderColor: C.border,
        backgroundColor: C.fill,
        borderWidth: 1.5,
        pointRadius: 2,
        pointHoverRadius: 3,
        pointBackgroundColor: C.point,
        pointBorderColor: C.border,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,                     // štvorcový graf – drží sa šírky .chart-sm
      layout: { padding: 8 },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: C.text, boxWidth: 10, boxHeight: 10, font: { size: 11 } }
        },
        tooltip: { enabled: true }
      },
      scales: {
        r: {
          min: 1, max: 7,
          ticks: { stepSize: 1, color: C.text, font: { size: 10 }, backdropColor: 'transparent' },
          grid: { color: C.grid, lineWidth: 0.75 },
          angleLines: { color: C.angle, lineWidth: 0.75 },
          pointLabels: { color: C.text, font: { size: 11 } }
        }
      }
    }
  });
  },

  destroyRadar () {
    if (this.radar && typeof this.radar.destroy === 'function') {
      this.radar.destroy();
      this.radar = null;
    }
  },

  getBaseTextColor() {
    const base = getComputedStyle(this.$el).color || '#222';
    return base;
  },
  toRGBA(color, alpha=1) {
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = color;
    const parsed = ctx.fillStyle; // "rgb(r, g, b)"
    const m = parsed.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    const [_, r, g, b] = m || [0, 34, 34, 34];
    return `rgba(${r||34}, ${g||34}, ${b||34}, ${alpha})`;
  },
  radarColorsUniversal() {
    const text = this.getBaseTextColor();
    return {
      text,
      grid:  this.toRGBA(text, 0.22),
      angle: this.toRGBA(text, 0.30),
      border: '#14b8a6',
      fill:   'rgba(20, 184, 166, 0.22)',
      point:  '#14b8a6'
    };
  },

  writeAnswer(_final) {
    const roundedMeans = Object.fromEntries(
      Object.entries(this.means).map(([k, v]) => [k, Math.round(v * 1000) / 1000])
    );
    const _data = {
      id: 0,
      test_id: this.$route.params.id,
      score: -1,
      answers: JSON.stringify(this.answers),
      final: _final,
      rslt: JSON.stringify(roundedMeans),
      agreement: this.agreement,
      time_length: this.startTime ? Math.round((Date.now() - this.startTime)/1000) : 0
    };
    axios.post('/write-my-person-test-answer', _data, this.$store.getters.getAxiosConfig)
      .catch(error => this.$store.commit('setFeedback', error));
  },

  closeTest() {
    this.$router.push({ name: 'personalisation_tests', params: {} });
  },
}
  }

</script>

<style scoped>
  pre { white-space: pre-wrap; word-break: break-word; }
  .chart-wrap { position: relative; width: 100%; }
  .chart-sm   { max-width: 420px; margin: 0 auto; } /* cieľová veľkosť radaru */
  .chart-wrap canvas { display: block; width: 100% !important; height: auto !important; }
</style>
